import { FormOutlined } from '@ant-design/icons';
import { notification } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import Highcharts from 'highcharts';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DashboardContext from '../../../contexts/DashboardContext';
import { useDashboardDataContext } from '../DashboardDataContext';
import { useExpandedParamsCardContext } from './ExpandedParamsCardContext';

import SourceList from '../Components/SourceList/SourceList';
import DownloadReportModal from '../ParamsCard/DownloadReportModal';
import ForecastSelector from '../ParamsCard/ForecastSelector';
import ReadingsGraph from '../ParamsCard/ReadingsGraph';
import ResumeTable from '../ParamsCard/ResumeTable';
import { ExpandedParamsCardView } from './ExpandedParamsCardView';
import AnnotationForm from './components/AnnotationForm';
import AnnotationsList from './components/AnnotationsList';
import NotesForParamsModal from './components/NotesForParamsModal';
import { ParameterTable } from './components/ParameterTable';
import SynchronizedCharts from './components/SynchronizedCharts';

import { useForecastSimulations } from '../../../hooks/useForecastSimulations';
import { useGraphBuilder } from '../../../hooks/useGraphBuilder';
import { useOtherSourcesByInfra } from '../../../hooks/useOtherSourcesByInfra';
import { useReportGenerator } from '../../../hooks/useReportGenerator';
import { useSourcesHealth } from '../../../hooks/useSourcesHealth';
import { useAnnotations } from './hooks/useAnnotations';
import { useTableData } from './hooks/useTableData';

import {
	addReadingToListThunk,
	deleteReadingFromList,
	deleteResumeAndReadingToList,
	getAllListThunk,
} from '../../../redux/slices/readingsResumesSlice';

import AnnotationsService from '../../../domain/Annotations/AnnotationsService';
import { paramsCardEventsService } from '../../../events';

const annotationService = new AnnotationsService();
const subscriptionPC$ = paramsCardEventsService.getSubject();

import SimulationsService from '@/domain/Simulations/SimulationsService';
import Icon from '@ant-design/icons';
import { TableRowSelection } from 'antd/es/table/interface';
import { ParameterIconTableSvg, TrashIconSvg } from '../../../assets/svg';
import { generateColor } from '../../../utils/DashboardGraphs';
import { generateUsageColor } from '../../../utils/generateUsageColor';
import { getIsDateRangeMoreThan24Hours } from '../ParamsCard/ParamsCard.helpers';
import { formatNumber, getNewElements, scrollToTop } from './utils/expandedParamsHelpers';
import { Button, Modal, Toggle, Tooltip } from '@hydrodigital/design-system';

const simulationsService = new SimulationsService();

export const ExpandedParamsCardContainer: React.FC = () => {
	const dispatch = useDispatch();

	const { value: dashboardContext, setValue: setDashboardContext } = useContext(DashboardContext);
	const dashboardDataContext = useDashboardDataContext();
	const expandedContext = useExpandedParamsCardContext();

	const readingsList = useSelector((state: any) => state.readingsResumes?.readings || []);
	const resumeList = useSelector((state: any) => state.readingsResumes?.resumes || []);
	const isLoading = useSelector((state: any) => state.readingsResumes?.isLoading || false);

	const chartRef = useRef<any>(null);
	const strategiesChartRef = useRef<any>(null);
	const topRef = useRef<HTMLDivElement>(null);
	const previousDataRef = useRef<Map<any, any>>(new Map());
	const previousParamsRef = useRef<any[]>([]);
	const processedParametersRef = useRef<Set<number>>(new Set());

	const [isReportModalOpen, setIsReportModalOpen] = useState(false);
	const [showAnnotationModal, setShowAnnotationModal] = useState(false);
	const [showAnnotationListModal, setShowAnnotationListModal] = useState(false);
	const [showParamAnnotationModal, setShowParamAnnotationModal] = useState(false);
	const [annotationMode, setAnnotationMode] = useState(false);
	const [annotationSaved, setAnnotationSaved] = useState(false);
	const [noteClicked, setNoteClicked] = useState(false);
	const [isParamAnnotation, setIsParamAnnotation] = useState(false);
	const [activeAnnotationIcon, setActiveAnnotationIcon] = useState<string | null>(null);
	const [isSavingAnnotation, setIsSavingAnnotation] = useState(false);
	const [globalForecastResults, setGlobalForecastResults] = useState<any[]>([]);
	const [dynamicStats, setDynamicStats] = useState<any>({});
	const [dates, setDates] = useState<any>(null);
	const [strategiesResponse, setStrategiesResponse] = useState<any[]>([]);
	const [isLoadingStrategies, setIsLoadingStrategies] = useState(false);
	const [hasValidStrategies, setHasValidStrategies] = useState(false);

	const {
		queryParams,
		setQueryParams,
		expandedRowKeys,
		setExpandedRowKeys,
		buildDataToTable,
		dataTable,
		setDataTable,
	} = useTableData();

	const cleanAreaSeries = () => {
		const chart = chartRef.current?.chart;
		const strategiesChart = strategiesChartRef.current?.chart;
		if (chart?.series) {
			chart.series.filter((s) => s.type === 'arearange').forEach((s) => s.remove(false));
			chart.redraw();
		}
		if (strategiesChart?.series) {
			strategiesChart.series.filter((s) => s.type === 'arearange').forEach((s) => s.remove(false));
			strategiesChart.redraw();
		}
		setPastForecastData([]);
	};

	const handleSimulationSelected = (simulationId) => {};

	const resetZoom = useCallback(() => {
		const chart = chartRef.current?.chart;
		const strategiesChart = strategiesChartRef.current?.chart;

		if (chart?.xAxis) {
			chart.xAxis.forEach((axis) => axis.setExtremes(undefined, undefined));
		}

		if (strategiesChart?.xAxis) {
			strategiesChart.xAxis.forEach((axis) => axis.setExtremes(undefined, undefined));
		}
	}, [chartRef, strategiesChartRef]);
	const {
		simulationsList,
		selectedSimulation,
		fetchSimulations,
		setSelectedSimulation,
		pastForecastData,
		setPastForecastData,
		showPastForecast,
		setShowPastForecast,
		isLoadingSimulations,
		hasSimulationError,
	} = useForecastSimulations(
		readingsList,
		dashboardContext.selectedSensors,
		dashboardContext.selectedDate,
		handleSimulationSelected,
		cleanAreaSeries,
	);

	// ✅ Use useRef to maintain updated context reference
	const dashboardContextRef = useRef(dashboardContext);
	dashboardContextRef.current = dashboardContext;

	// ✅ Create refs for functions needed in handleChartClick
	const functionsRef = useRef({
		setAnnotationListToShow: (annotations: any[]) => {}, // Function dummy initial
		setShowAnnotationListModal: setShowAnnotationListModal,
		filterAnnotationsByDate: (unixDate: number) => [] as any[], // Function dummy initial
	});

	const handleChartClick = (event: any) => {
		const currentContext = dashboardContextRef.current;

		if (!currentContext.showParamsCard) {
			return;
		}

		if (!currentContext.isExpandedParamsCard) {
			return;
		}

		const localOffset = dayjs().utcOffset() * 60 * 1000;
		const clickTime = event.xAxis[0].value - localOffset;

		const annotations = functionsRef.current.filterAnnotationsByDate(clickTime);

		functionsRef.current.setAnnotationListToShow(annotations);
		functionsRef.current.setShowAnnotationListModal(annotations.length > 0);
	};

	const { graphOptions, removeAllPlotBands, addPlotBand, removePlotBandsByIds } = useGraphBuilder({
		// ✅ Cuando isExpandedParamsCard, usar strategiesChartRef para ambos (es un solo chart combinado)
		chartRef: dashboardContext.isExpandedParamsCard ? strategiesChartRef : chartRef,
		strategiesChartRef,
		readingsList,
		pastForecastData,
		expanded: true,
		annotationMode,
		onClickChart: dashboardContext.isExpandedParamsCard ? handleChartClick : undefined,
		onRangeSelected: handleRangeSelected,
	});

	const {
		annotationListToShow,
		quality,
		comment,
		title,
		annotationsList,
		sourceToAnnotation,
		setQuality,
		setComment,
		setTitle,
		setAnnotationsList,
		setAnnotationListToShow,
		setSourceToAnnotation,
		checkQualityForNewAnnotation,
		filterAnnotationsByDate,
		handleGetAnnotations,
	} = useAnnotations({
		readingsList,
		addPlotBandToChart: addPlotBand,
		removeAllPlotBands,
		removePlotBandsByIds,
		selectedDates: dashboardContext.selectedDate,
		sourcesByParam: dashboardDataContext.sourcesByParam,
		seriesVisibilityState: dashboardDataContext.seriesVisibilityState,
	});

	useEffect(() => {
		functionsRef.current = {
			setAnnotationListToShow,
			setShowAnnotationListModal,
			filterAnnotationsByDate,
		};
	}, [setAnnotationListToShow, setShowAnnotationListModal, filterAnnotationsByDate]);

	const { fetchHealthDataByParameterId } = useSourcesHealth({ autoFetch: false });

	const { generateReport } = useReportGenerator(dashboardContext.selectedDate);

	const showButton = useMemo(() => {
		if (
			!readingsList ||
			readingsList.length === 0 ||
			dashboardContext.selectedSensors?.length === 0
		) {
			return false;
		}

		const seen = new Set();
		const validReadings = readingsList.filter((item) => {
			if (!item) return false;

			const key = JSON.stringify([item.originalParameterId, item.originalSourceSerialNumber]);
			if (seen.has(key)) return false;
			seen.add(key);

			return (
				item.originalSourceSerialNumber == null &&
				dashboardContext.selectedSensors.some(
					(sensor) => sensor.serialNumberTool === item.serialNumberMeasurementTool,
				)
			);
		});

		return validReadings.length === 1;
	}, [readingsList, dashboardContext.selectedSensors]);

	const isDateRangeMoreThan24Hours = useMemo(
		() => getIsDateRangeMoreThan24Hours(dashboardContext.selectedDate),
		[dashboardContext.selectedDate],
	);

	// ✅ When showButton becomes false (e.g., 2+ params), clean up forecast state
	useEffect(() => {
		if (!showButton && showPastForecast) {
			cleanAreaSeries();
			setShowPastForecast(false);
			setSelectedSimulation(undefined);
		}
	}, [showButton]);

	useEffect(() => {
		if (pastForecastData && pastForecastData.length > 0) {
			setGlobalForecastResults(pastForecastData as any);
		}
	}, [pastForecastData]);
	useEffect(() => {
		if (globalForecastResults && globalForecastResults.length > 0) {
			// Here you could update the graph with the new forecast data
			// The graph update logic should be in buildDataToGraph
		}
	}, [globalForecastResults]);

	const handleAnnotationToggle = useCallback(
		(isActive: boolean, sourceKey?: string, sourceSerialNumber?: string) => {
			if (isActive && sourceKey) {
				setActiveAnnotationIcon(sourceKey);
				setAnnotationMode(true);
				if (sourceSerialNumber) {
					dashboardDataContext.setSelectedSourceToAnnotation(sourceSerialNumber);
				}
			} else {
				// Deactivating annotation mode
				setActiveAnnotationIcon(null);
				setAnnotationMode(false);
				dashboardDataContext.setSelectedSourceToAnnotation('');
			}
		},
		[],
	);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (!annotationMode && !noteClicked) return;

			const target = event.target as HTMLElement;

			const chartElement = chartRef.current?.renderTo || chartRef.current?.container;
			const strategiesChartElement =
				strategiesChartRef.current?.renderTo || strategiesChartRef.current?.container;
			const isClickInsideChart =
				(chartElement && chartElement instanceof Element && chartElement.contains(target)) ||
				(strategiesChartElement &&
					strategiesChartElement instanceof Element &&
					strategiesChartElement.contains(target)) ||
				target.closest('.highcharts-container') ||
				target.classList.contains('highcharts-background') ||
				target.closest('.highcharts-background') ||
				target.classList.contains('highcharts-root') ||
				target.closest('svg')?.querySelector('.highcharts-background');

			const isClickOnAnnotationIcon =
				target.closest('[data-annotation-icon="true"]') ||
				target.closest('[data-icon="form"]') ||
				(target.closest('.anticon') && target.closest('div')?.textContent?.includes('Recommended'));

			const isClickOnNoteButton =
				target.textContent?.includes('Create a Note') ||
				target.closest('span')?.textContent?.includes('Create a Note') ||
				target.closest('[class*="cursor-pointer"]')?.textContent?.includes('Create a Note');

			const shouldDisableAnnotationMode =
				!isClickInsideChart && !isClickOnAnnotationIcon && !isClickOnNoteButton;

			if (shouldDisableAnnotationMode) {
				if (annotationMode) {
					setAnnotationMode(false);
					setActiveAnnotationIcon(null);
					dashboardDataContext.setSelectedSourceToAnnotation('');
					setAnnotationSaved((prev) => !prev);
				}
				if (noteClicked) {
					setNoteClicked(false);
					setIsParamAnnotation(false);
				}
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [annotationMode, noteClicked, activeAnnotationIcon]);

	function handleRangeSelected(event) {
		if (event) {
			const xMin = event.xAxis[0].min;
			const xMax = event.xAxis[0].max;

			const chart = event.target;
			const serie = chart.series.find(
				(x) =>
					!x.userOptions.isForecast &&
					x.userOptions.serieType === 'source' &&
					x.userOptions.sourceSerialNumber === dashboardDataContext.selectedSourceToAnnotation,
			);
			const rangePoints = serie?.points.filter((x) => x.x >= xMin && x.x <= xMax);

			const firstPoint = rangePoints?.[0];
			const lastPoint = rangePoints?.[rangePoints.length - 1];
			if (firstPoint && lastPoint) {
				dashboardDataContext.setSelectedQualityAnnotationRange({
					start: firstPoint.quality,
					end: lastPoint.quality,
				});
			}
		}
		setAnnotationMode((current) => {
			if (current) {
				event.preventDefault();
				setDates([
					Highcharts.dateFormat('%Y-%m-%d %H:%M:%S', event.xAxis[0].min),
					Highcharts.dateFormat('%Y-%m-%d %H:%M:%S', event.xAxis[0].max),
				]);

				setIsParamAnnotation((current) => {
					if (current) {
						setShowParamAnnotationModal(true);
					} else {
						setShowAnnotationModal(true);
					}
					return current;
				});
			}
			return false;
		});
	}

	const columnsAnnotationsModal = useMemo(
		() => [
			{
				title: 'Type',
				dataIndex: 'groupId',
				key: 'groupId',
				align: 'center',
				render: (_, record) => {
					return record.groupId ? 'Op. Note' : 'Qual. Annot.';
				},
			},
			{
				title: 'Name',
				dataIndex: 'title',
				key: 'title',
				width: 200,
			},
			{
				title: 'Asset Param Source',
				key: '',
				render: (_, record) => {
					return (
						<>
							<span>{`${record.infraName}`}</span>
							<br />
							<span>{record.paramName}</span>
							<br />
							<span>{record.sourceName}</span>
						</>
					);
				},
			},
			{
				title: 'Comment',
				dataIndex: 'comment',
				key: 'comment',
				render: (_, record) => (
					<Tooltip title={record.comment} trigger="hover">
						<div
							className={
								record.comment.length > 20
									? 'text-xs overflow-hidden whitespace-nowrap text-ellipsis w-[200px]'
									: 'text-xs'
							}
						>
							{record.comment}
						</div>
					</Tooltip>
				),
			},
			{
				title: 'Data Quality',
				dataIndex: 'quality',
				key: 'quality',
				align: 'center',
			},
			{
				title: 'Start',

				key: 'startDate',
				render: (_, record) => (
					<span className="text-xs">{`${dayjs(record.startDate).format('YYYY/MM/DD [at] hh:mm a')}`}</span>
				),
			},
			{
				title: 'End',
				key: 'endDate',
				render: (_, record) => (
					<span className="text-xs">{`${dayjs(record.endDate).format('YYYY/MM/DD [at] hh:mm a')}`}</span>
				),
			},
			{
				title: 'Created by',
				dataIndex: 'createdBy',
				key: 'createdBy',
				width: 200,
				render: (_, record) => (
					<div className="flex column">
						<span>{`${record.createdBy}`}</span>
						<span style={{ color: '#74788D', fontSize: '10px' }}>{`${dayjs(record.createdAt).format(
							'YYYY/MM/DD [at] hh:mm a',
						)}`}</span>
					</div>
				),
			},
			// {
			// 	title: 'Group ID',
			// 	dataIndex: 'groupId',
			// 	key: 'groupId',
			// 	align: 'center'
			// },
		],
		[],
	);

	async function handleSaveSourcesAnnotation() {
		setIsSavingAnnotation(true);
		try {
			// ✅ Calculate UTC offset to adjust dates before sending to API
			const offsetInMs = dayjs().utcOffset() * 60 * 1000;

			const data = {
				serialNumber: sourceToAnnotation.serialNumber,
				parameterId: [sourceToAnnotation.parameterId],
				// ✅ Subtract offset because dates from graph already have offset applied
				startDate: dayjs(dates?.[0])
					.subtract(offsetInMs, 'milliseconds')
					.format('YYYY-MM-DD HH:mm:ss'),
				endDate: dayjs(dates?.[1])
					.subtract(offsetInMs, 'milliseconds')
					.format('YYYY-MM-DD HH:mm:ss'),
				quality,
				comment,
				title,
			};
			const res = await annotationService.postAnnotations(data);
			if (res) {
				notification.success({
					message: 'Successful',
					description: 'Annotation created successfully',
				});
				handleGetAllReadings();
				setTitle('');
				setComment('');
				setQuality(0);

				setAnnotationSaved(true);
				setTimeout(() => {
					setAnnotationMode(false);
					dashboardDataContext.setSelectedSourceToAnnotation('');
					setActiveAnnotationIcon(null);

					setAnnotationSaved((prev) => false);
				}, 100);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsSavingAnnotation(false);
			setShowAnnotationModal(false);
		}
	}

	function handleGetAllReadings() {
		const otherSourcesSelected = dashboardDataContext.selectedSources.filter((source) => {
			return dashboardContext.selectedSensors.find(
				(sensor) => sensor.serialNumberMeasurementTool !== source.serialNumber,
			);
		});
		const [dateFrom, dataTo] = dashboardContext.selectedDate;
		dispatch(getAllListThunk(dashboardContext.selectedSensors, dateFrom, dataTo) as any).then(
			() => {
				otherSourcesSelected.forEach((os) => {
					dispatch(
						addReadingToListThunk(
							os.parameterId,
							os.serialNumber,
							dateFrom,
							dataTo,
							os.sourceId,
							true,
						) as any,
					);
				});
			},
		);
	}

	const findRecord = useCallback(
		(paramId: string, serialNumber: string) => {
			return readingsList.find(
				(rl) => rl.originalParameterId == paramId && rl.originalSourceSerialNumber == serialNumber,
			);
		},
		[readingsList],
	);

	const {
		getOtherSourcesByInfra,
		clearSourcesInitialized,
		getGlobalSourcesInitialized,
		setGlobalSourcesInitialized,
		updateInfrastructureName,
	} = useOtherSourcesByInfra({
		toggleSeriesVisibility: dashboardDataContext.toggleSeriesVisibility,
		fetchHealthDataByParameterId: (parameterId: number) =>
			fetchHealthDataByParameterId(parameterId),
		setSourceToAnnotation,
		setAnnotationMode,
		scrollToTop,
		topRef,
		handleGetAnnotations,
	});

	const handleShowOtherSources = async (record: any) => {
		await getOtherSourcesByInfra(record);
		setExpandedRowKeys((oldValue) => {
			return [...oldValue, record.key];
		});
	};

	//useDataTable
	const columnsExpanded = useMemo(
		() => [
			{
				id: 'graph',
				header: 'Graph',
				width: 60,
				cell: ({ row }) => {
					const record = row.original;
					const color = generateColor(record.parameterId, null);
					return <ParameterIconTableSvg color={color} />;
				},
			},
			{
				id: 'infrastructureName',
				header: 'Asset',
				accessorKey: 'infrastructureName',
				key: 'infrastructureName',
				width: 200,
				cell: ({ getValue }) => <span>{getValue()}</span>,
			},
			{
				id: 'parameter',
				header: 'Parameter',
				accessorKey: 'parameter',
				key: 'parameter',
				width: 200,
				cell: ({ getValue }) => <span>{getValue()}</span>,
			},
			{
				id: 'type',
				header: 'Type',
				accessorKey: 'type',
				key: 'type',
				width: 80,
				cell: ({ getValue }) => <span>{getValue()}</span>,
			},
			{
				id: 'unit',
				header: 'Units',
				accessorKey: 'unit',
				key: 'unit',
				width: 80,
				cell: ({ getValue }) => <span>{getValue()}</span>,
			},
			{
				id: 'currentValue',
				header: 'Last Value',
				cell: ({ row, getValue }) => (
					<>
						<span>
							{row.original.currentValue !== null ? `${row.original.currentValue}` : 'No data'}
						</span>
					</>
				),
				width: 90,
			},
			{
				id: 'usage',
				header: 'Capacity',
				cell: ({ row, getValue }) => (
					<div className="flex items-center gap-2 overflow-hidden">
						{row.original.usage !== null ? (
							<div
								className="w-3 h-3 rounded-full"
								style={{ backgroundColor: generateUsageColor(row.original.usage) }}
							/>
						) : null}

						<Tooltip
							title={
								row.original.usage !== null
									? `${row.original.usage > 0 ? row.original.usage.toFixed(2) : row.original.usage} %`
									: 'No data'
							}
						>
							<span className="overflow-hidden whitespace-nowrap text-ellipsis w-20">
								{row.original.usage !== null
									? `${row.original.usage > 0 ? row.original.usage.toFixed(2) : row.original.usage} %`
									: 'No data'}
							</span>
						</Tooltip>
					</div>
				),
				width: 100,
			},
			{
				id: 'lastCollected',
				header: 'Last Collected',
				cell: ({ row, getValue }) => (
					<span>
						{row.original.lastCollected && row.original.lastCollected != 'N/A'
							? `${dayjs(row.original.lastCollected).format('YYYY/MM/DD hh:mm A')}`
							: 'No data available'}
					</span>
				),
				width: 180,
			},
			{
				id: 'maximun',
				header: 'Max.',
				cell: ({ row, getValue }) => {
					const paramStats = dynamicStats[row.original.parameterId];
					const maxValue =
						paramStats?.max !== undefined
							? formatNumber(paramStats.max)
							: row.original.maximun !== undefined
								? formatNumber(row.original.maximun)
								: '0';
					return <span>{maxValue}</span>;
				},
				width: 90,
			},
			{
				id: 'minimum',
				header: 'Min.',
				cell: ({ row, getValue }) => {
					const paramStats = dynamicStats[row.original.parameterId];
					const minValue =
						paramStats?.min !== undefined
							? formatNumber(paramStats.min)
							: row.original.minimum !== undefined
								? formatNumber(row.original.minimum)
								: '0';
					return <span>{minValue}</span>;
				},
				width: 90,
			},
			{
				id: 'sum',
				header: 'Sum',
				cell: ({ row, getValue }) => {
					const paramStats = dynamicStats[row.original.parameterId];
					return (
						<span>
							{paramStats?.sum !== undefined
								? formatNumber(paramStats.sum)
								: formatNumber(row.original.sum)}
						</span>
					);
				},
				width: 100,
			},
			{
				id: 'average',
				header: 'Average',
				cell: ({ row, getValue }) => {
					const paramStats = dynamicStats[row.original.parameterId];
					return (
						<span>
							{paramStats?.average !== undefined
								? formatNumber(paramStats.average)
								: formatNumber(row.original.average)}
						</span>
					);
				},
				width: 90,
			},
			{
				id: 'criticalHigh',
				header: 'Critical High',
				cell: ({ row, getValue }) => <span>{`${row.original.criticalHigh}`}</span>,
				width: 90,
			},
			{
				id: 'criticalLow',
				header: 'Critical Low',
				cell: ({ row, getValue }) => <span>{`${row.original.criticalLow}`}</span>,
				width: 90,
			},
			{
				id: 'actions',
				header: '',
				cell: ({ row, getValue }) => (
					<Tooltip text="Remove" position="up" trigger="hover">
						<span>
							<Icon
								component={() => <TrashIconSvg color="#74788D" />}
								style={{ color: '#74788D', fontSize: '20px', cursor: 'pointer' }}
								onClick={() => {
									clearSourcesInitialized(row.original.parameterId);

									dashboardDataContext.setSourcesByParam((oldValue) => {
										const filtered = oldValue.filter((sp) => sp.id != row.original.parameterId);
										return filtered;
									});
									dashboardDataContext.setSelectedParamsList((selectedParams) => {
										let copy = [...selectedParams];
										const paramToDelete = copy.find((param) => {
											return param.parameterId == row.original.parameterId;
										});
										paramToDelete.sourceList.forEach((source) => {
											if (source.sourceId) {
												dispatch(
													deleteReadingFromList(
														paramToDelete.parameterId,
														source.serialNumber,
													) as any,
												);
											} else {
												dispatch(
													deleteResumeAndReadingToList(paramToDelete.parameterId, null) as any,
												);
											}
										});
										copy = copy.filter((param) => param.parameterId != row.original.parameterId);
										return copy;
									});

									setDashboardContext((currentContext) => {
										const newSelectedSensors = currentContext.selectedSensors.filter((sensor) => {
											return sensor.measurementTargetId != row.original.parameterId;
										});
										if (newSelectedSensors.length === 0) {
											dashboardDataContext.setShowSources(false);
										}
										return {
											...currentContext,
											isExpandedParamsCard: newSelectedSensors.length != 0,
											selectedSensors: newSelectedSensors,
										};
									});
								}}
							/>
						</span>
					</Tooltip>
				),
				width: 60,
			},
		],
		[dataTable, queryParams, dynamicStats, dashboardDataContext.seriesVisibilityState],
	);

	const paramsToShowInTable = useCallback(() => {
		if (queryParams != '') {
			return dataTable.filter((md) => {
				return md.parameter.includes(queryParams);
			});
		} else {
			return dataTable;
		}
	}, [queryParams, dataTable]);

	const onSelectInfrastructure = useCallback(
		(record: any, selected: boolean, selectedRows: any[]) => {
			if (selected) {
				dashboardDataContext.toggleSeriesVisibility(record);
			} else {
				dashboardDataContext.setSourcesByParam((currentSources) => {
					const paramSources = currentSources.find((ps) => ps.id === record.parameterId);
					if (paramSources) {
						paramSources.sources.forEach((source) => {
							if (source.checked) {
								if (source.sourceId === null && source.serialNumber === null) {
									// Toggle visibility AND remove from readingsList
									dashboardDataContext.toggleSeriesVisibility(record);
									// Use 'recommended' as serialNumber to identify it in readingsList
									dispatch(deleteReadingFromList(record.parameterId, 'recommended') as any);
								} else {
									dispatch(deleteReadingFromList(record.parameterId, source.serialNumber) as any);
								}
							}
						});
					}
					return currentSources;
				});
			}

			dashboardDataContext.setSourcesByParam((currentSources) => {
				const updatedSources = currentSources.map((paramSource) => {
					if (paramSource.id === record.parameterId) {
						if (selected) {
							const updatedParamSource = paramSource.sources.map((source) => ({
								...source,
								checked: source.name === 'Recommended' && source.sourceId === null,
							}));
							const otherSource = paramSource.sources.filter(
								(source) => source.name !== 'Recommended' && source.sourceId !== null,
							)[0];

							// ❌ REMOVED: Duplicate call to handleGetAnnotations
							// useAnnotations hook already handles this reactively via readingsList

							return {
								...paramSource,
								sources: updatedParamSource,
							};
						} else {
							const updatedParamSource = paramSource.sources.map((source) => ({
								...source,
								checked: false,
							}));

							return {
								...paramSource,
								sources: updatedParamSource,
							};
						}
					}
					return paramSource;
				});

				return updatedSources;
			});

			if (!selected) {
				setTimeout(() => {
					clearSourcesInitialized(record.parameterId);
				}, 0);
			}

			dashboardDataContext.setSelectedRowKeys((currentKeys) => {
				const key = record.key;
				return selected ? [...currentKeys, key] : currentKeys.filter((k) => k !== key);
			});
		},
		[
			dashboardDataContext.toggleSeriesVisibility,
			dashboardDataContext.seriesVisibilityState,
			dashboardDataContext.setSeriesVisibilityState,
			dashboardDataContext.setSourcesByParam,
			dashboardDataContext.setSelectedParamsList,
			setDashboardContext,
			dispatch,
		],
	);

	// Convert selectedRowKeys array to RowSelectionState object (Record<string, boolean>)
	const rowSelectionState = useMemo<Record<string, boolean>>(() => {
		const selectionObj: Record<string, boolean> = {};
		dashboardDataContext.selectedRowKeys.forEach((key) => {
			const stringKey = String(key);
			selectionObj[stringKey] = true;
		});
		return selectionObj;
	}, [dashboardDataContext.selectedRowKeys]);

	const disableDownloadReport = dashboardDataContext.showSources && (
		dashboardDataContext.selectedRowKeys.length === 0 ||
		!dashboardDataContext.sourcesByParam.some((param: any) =>
			param.sources.some((s: any) => s.checked),
		)
	);

	const sensorsForReport = useMemo(() => {
		if (!dashboardDataContext.showSources) {
			return dashboardContext.selectedSensors;
		}
		const result: any[] = [];
		dashboardDataContext.selectedRowKeys.forEach((key: any) => {
			const paramId = key;
			const paramSources = dashboardDataContext.sourcesByParam.find((p: any) => p.id === paramId);
			if (!paramSources) return;
			const baseSensor = dashboardContext.selectedSensors.find(
				(s: any) => s.measurementTargetId === paramId,
			);
			if (!baseSensor) return;
			paramSources.sources
				.filter((s: any) => s.checked)
				.forEach((s: any) => {
					result.push({ ...baseSensor, sourceId: s.sourceId });
				});
		});
		return result;
	}, [
		dashboardDataContext.showSources,
		dashboardDataContext.selectedRowKeys,
		dashboardDataContext.sourcesByParam,
		dashboardContext.selectedSensors,
	]);

	// Handle row selection changes from DataGrid (TanStack Table OnChangeFn)
	// The DataGrid updater may not preserve previously selected rows in the returned object,
	// so we detect the single toggled row by comparing old vs new selection state.
	const handleRowSelectionChange = useCallback(
		(
			updaterOrValue:
				| Record<string, boolean>
				| ((old: Record<string, boolean>) => Record<string, boolean>),
		) => {
			const newSelection =
				typeof updaterOrValue === 'function' ? updaterOrValue(rowSelectionState) : updaterOrValue;

			// Find which key was toggled by comparing old and new selection
			const oldKeys = new Set(Object.keys(rowSelectionState).filter((k) => rowSelectionState[k]));
			const newKeys = new Set(Object.keys(newSelection).filter((k) => newSelection[k]));

			// Detect the single added or removed key
			let addedKey: string | null = null;
			let removedKey: string | null = null;

			for (const k of newKeys) {
				if (!oldKeys.has(k)) {
					addedKey = k;
					break;
				}
			}
			for (const k of oldKeys) {
				if (!newKeys.has(k)) {
					removedKey = k;
					break;
				}
			}

			// Process only the single toggled row
			if (addedKey) {
				const record = dataTable.find((item) => String(item.key) === String(addedKey));
				if (record) {
					onSelectInfrastructure(record, true, []);
				}
			}

			if (removedKey) {
				const record = dataTable.find((item) => String(item.key) === String(removedKey));
				if (record) {
					onSelectInfrastructure(record, false, []);
				}
			}
		},
		[rowSelectionState, dataTable, onSelectInfrastructure],
	);

	useEffect(() => {
		const seen = new Set();
		let data: any[] = [];

		if (resumeList && resumeList.length > 0) {
			data = resumeList.filter((item) => {
				if (item) {
					const tuple = JSON.stringify([item.parameterId]);

					if (seen.has(tuple)) {
						return false;
					}

					seen.add(tuple);
				}
				return (
					item !== undefined &&
					dashboardDataContext.selectedParamsList.find(
						(param) => param.parameterId == item.parameterId,
					)
				);
			});
		}

		const resumeParamIds = new Set(data.map((item) => item.parameterId));
		dashboardDataContext.selectedParamsList.forEach((param) => {
			if (!resumeParamIds.has(param.parameterId)) {
				data.push({
					parameterId: param.parameterId,
					parameterName: param.parameterName || param.measurementTargetName || 'Parameter',
					currentValue: null,
					unit: param.unit || '',
					usage: 0,
					lastCollected: null,
					criticalHigh: null,
					criticalHhigh: null,
					criticalLow: null,
					criticalLlow: null,
					sourceName: param.infrastructureName || 'Infrastructure',
					infrastructureName: param.infrastructureName || 'Infrastructure',
					max: null,
					min: null,
					average: null,
					sum: null,
				});
			}
		});

		const dataSource = buildDataToTable(data, dashboardDataContext.selectedParamsList);

		// Add 'id' field to each item for DataGrid row identification
		// DataGrid's getRowId looks for 'id' field by default
		const dataSourceWithId = dataSource.map((item) => ({
			...item,
			id: item.key, // Map key to id for DataGrid
		}));

		dashboardDataContext.setSelectedRowKeys((prevKeys) => {
			const availableKeys = dataSourceWithId.map((item) => item.key);
			const currentParamIds = dashboardDataContext.selectedParamsList.map(
				(param) => param.parameterId,
			);
			const previousParamIds = previousParamsRef.current;

			if (previousParamIds.length === 0) {
				previousParamsRef.current = currentParamIds;
				return availableKeys;
			}

			const addedParams = currentParamIds.filter((id) => !previousParamIds.includes(id));

			if (addedParams.length > 0) {
				const newKeys = availableKeys.filter((key) => {
					const paramId = dataSourceWithId.find((item) => item.key === key)?.parameterId;
					return addedParams.includes(paramId);
				});
				const updatedKeys = [...prevKeys, ...newKeys];
				previousParamsRef.current = currentParamIds;
				return updatedKeys;
			} else {
				const filteredKeys = prevKeys.filter((key) => availableKeys.includes(key));
				previousParamsRef.current = currentParamIds;
				return filteredKeys;
			}
		});
		setDataTable(dataSourceWithId);
	}, [resumeList, dashboardDataContext.selectedParamsList]);

	const updatedInfrastructureNamesRef = useRef<Set<number>>(new Set());

	useEffect(() => {
		const infrastructureMap = new Map<number, string>();
		dataTable.forEach((item) => {
			if (item.infrastructureName && item.infrastructureName !== 'Infrastructure') {
				infrastructureMap.set(item.parameterId, item.infrastructureName);
			}
		});

		if (infrastructureMap.size === 0) return;

		const sourcesToUpdate: Array<{ id: number; name: string }> = [];
		dashboardDataContext.sourcesByParam.forEach((source) => {
			const availableInfrastructureName = infrastructureMap.get(source.id);

			if (
				availableInfrastructureName &&
				source.name.includes('Infrastructure |') &&
				!updatedInfrastructureNamesRef.current.has(source.id)
			) {
				sourcesToUpdate.push({ id: source.id, name: availableInfrastructureName });
			}
		});

		sourcesToUpdate.forEach(({ id, name }) => {
			updateInfrastructureName(id, name);
			updatedInfrastructureNamesRef.current.add(id);
		});

		dashboardDataContext.setSelectedParamsList((currentParams) => {
			let hasChanges = false;
			const updatedParams = currentParams.map((param) => {
				const availableInfrastructureName = infrastructureMap.get(param.parameterId);

				if (
					availableInfrastructureName &&
					(!param.infrastructureName || param.infrastructureName === 'Infrastructure')
				) {
					hasChanges = true;
					return {
						...param,
						infrastructureName: availableInfrastructureName,
					};
				}
				return param;
			});

			return hasChanges ? updatedParams : currentParams;
		});
	}, [dataTable, updateInfrastructureName]);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			const infrastructureMap = new Map<number, string>();
			dataTable.forEach((item) => {
				if (item.infrastructureName && item.infrastructureName !== 'Infrastructure') {
					infrastructureMap.set(item.parameterId, item.infrastructureName);
				}
			});

			dashboardDataContext.sourcesByParam.forEach((source) => {
				const availableInfrastructureName = infrastructureMap.get(source.id);

				if (
					availableInfrastructureName &&
					source.name.includes('Infrastructure |') &&
					!updatedInfrastructureNamesRef.current.has(source.id)
				) {
					updateInfrastructureName(source.id, availableInfrastructureName);
					updatedInfrastructureNamesRef.current.add(source.id);
				}
			});
		}, 50);

		return () => clearTimeout(timeoutId);
	}, [dashboardDataContext.sourcesByParam.length]);

	useEffect(() => {
		if (dashboardDataContext.selectedRowKeys.length > 0) {
			const selectedParams = dataTable.filter((item) =>
				dashboardDataContext.selectedRowKeys.includes(item.key),
			);

			selectedParams.forEach(async (item) => {
				const alreadyProcessed = processedParametersRef.current.has(item.parameterId);
				const alreadyExists = dashboardDataContext.sourcesByParam.some(
					(source) => source.id === item.parameterId,
				);

				if (!alreadyProcessed && !alreadyExists) {
					processedParametersRef.current.add(item.parameterId);
					await handleShowOtherSources(item);
				}
			});
		}
	}, [dashboardDataContext.selectedRowKeys, dataTable]);

	useEffect(() => {
		const currentParameterIds = dataTable
			.filter((item) => dashboardDataContext.selectedRowKeys.includes(item.key))
			.map((item) => item.parameterId);

		const newProcessedSet = new Set<number>();
		const newUpdatedInfrastructureSet = new Set<number>();

		currentParameterIds.forEach((id) => {
			if (processedParametersRef.current.has(id)) {
				newProcessedSet.add(id);
			}
			if (updatedInfrastructureNamesRef.current.has(id)) {
				newUpdatedInfrastructureSet.add(id);
			}
		});

		processedParametersRef.current = newProcessedSet;
		updatedInfrastructureNamesRef.current = newUpdatedInfrastructureSet;
	}, [dashboardDataContext.selectedRowKeys, dataTable]);

	useEffect(() => {
		if (dashboardContext.selectedSensors.length > 0) {
			const selectedSs = dashboardContext.selectedSensors.map((sensor) => {
				return {
					paramName: sensor.measurementTargetName,
					parameterId: sensor.measurementTargetId,
					infrastructureId: sensor.infrastructureId,
					mainSerialNumber: sensor.serialNumberMeasurementTool,
					sourceList: [
						{
							sourceId: sensor.sourceId,
							serialNumber: sensor.serialNumberMeasurementTool,
						},
					],
				};
			});

			dashboardDataContext.setSelectedParamsList((prev) => {
				const paramsList = [...prev, ...getNewElements(selectedSs, prev)].filter((item) => {
					return selectedSs.some((sensor) => sensor.parameterId == item.parameterId);
				});
				dashboardDataContext.setSourcesByParam((prev) => {
					const currentSourcesByParam = prev;
					const newSourcesByParam = currentSourcesByParam.filter((sourceParam) => {
						return paramsList.some((paramItem) => paramItem.parameterId === sourceParam.id);
					});
					return newSourcesByParam;
				});
				return paramsList;
			});
		} else {
			dashboardDataContext.setSourcesByParam([]);
			dashboardDataContext.setShowSources(false);
			dashboardDataContext.setSelectedParamsList([]);
		}
	}, [dashboardContext.selectedSensors, dashboardContext.selectedDate]);

	useEffect(() => {
		dashboardDataContext.setSelectedSources((prev: any) => {
			return dashboardDataContext.selectedParamsList.reduce(
				(acum, param) => [
					...acum,
					...param.sourceList.map((source) => {
						return {
							parameterId: param.parameterId,
							sourceId: source.sourceId,
							serialNumber: source.serialNumber,
						};
					}),
				],
				[],
			);
		});
	}, [dashboardDataContext.selectedParamsList]);

	useEffect(() => {
		const otherSourcesSelected = dashboardDataContext.selectedSources.filter((source) => {
			return dashboardContext.selectedSensors.find(
				(sensor) => sensor.serialNumberMeasurementTool !== source.serialNumber,
			);
		});
		const [dateFrom, dataTo] = dashboardContext.selectedDate;
		dispatch(
			getAllListThunk(
				dashboardContext.selectedSensors,
				dashboardContext.selectedDate[0],
				dashboardContext.selectedDate[1],
			) as any,
		).then(() => {
			otherSourcesSelected.forEach((os) => {
				dispatch(
					addReadingToListThunk(
						os.parameterId,
						os.serialNumber,
						dateFrom,
						dataTo,
						os.sourceId,
						true,
					) as any,
				);
			});
		});
	}, [dashboardContext.selectedDate]);

	// ✅ Cargar estrategias al entrar a la vista expandida o cuando cambien las fechas
	useEffect(() => {
		const loadStrategies = async () => {
			if (!dashboardContext.selectedDate || !dashboardContext.selectedLocation) {
				return;
			}

			setIsLoadingStrategies(true);
			try {
				const response = await simulationsService.getRecommendedStrategies({
					startDate: dayjs(dashboardContext.selectedDate[0]).format('YYYY-MM-DD HH:mm:ss'),
					endDate: dayjs(dashboardContext.selectedDate[1]).format('YYYY-MM-DD HH:mm:ss'),
					locationId: dashboardContext.selectedLocation,
				});

				const strategies = response?.data || [];
				setStrategiesResponse(strategies);

				// ✅ Validate if there is at least 1 INP with non-default strategies
				const hasNonDefaultStrategies = strategies.some((inp: any) => {
					if (!inp.strategies || Object.keys(inp.strategies).length === 0) {
						return false;
					}
					// Verify if there is at least one strategy that is not "default"
					return Object.values(inp.strategies).some((strategy: any) => {
						return strategy?.strategy && strategy.strategy.toLowerCase() !== 'default';
					});
				});

				setHasValidStrategies(hasNonDefaultStrategies);

				// ✅ If there are no valid strategies, disable the toggle
				if (!hasNonDefaultStrategies && dashboardDataContext.showStrategies) {
					dashboardDataContext.setShowStrategies(false);
				}
			} catch (error) {
				console.error('Error fetching strategies:', error);
				setStrategiesResponse([]);
				setHasValidStrategies(false);
				if (dashboardDataContext.showStrategies) {
					dashboardDataContext.setShowStrategies(false);
				}
			} finally {
				setIsLoadingStrategies(false);
			}
		};

		loadStrategies();
	}, [dashboardContext.selectedDate, dashboardContext.selectedLocation]);

	// ✅ Reset zoom when dates change - set extremes to new date range
	useEffect(() => {
		if (dashboardContext?.selectedDate) {
			const [dateFrom, dateTo] = dashboardContext.selectedDate;
			// Extract local time components and treat as UTC to match chart data format
			// Works for both string and dayjs object inputs
			const dFrom = dayjs(dateFrom);
			const dTo = dayjs(dateTo);
			const newMin = Date.UTC(dFrom.year(), dFrom.month(), dFrom.date(), dFrom.hour(), dFrom.minute(), dFrom.second());
			const newMax = Date.UTC(dTo.year(), dTo.month(), dTo.date(), dTo.hour(), dTo.minute(), dTo.second());

			console.log('📅 [setExtremes] selectedDate changed:', {
				dateFrom,
				dateTo,
				localTimeFrom: dFrom.format('YYYY-MM-DD HH:mm:ss'),
				localTimeTo: dTo.format('YYYY-MM-DD HH:mm:ss'),
				newMin,
				newMax,
				'newMin as Date': new Date(newMin).toISOString(),
				'newMax as Date': new Date(newMax).toISOString(),
			});

			// Small delay to allow chart to process any pending updates
			const timer = setTimeout(() => {
				const chart = chartRef.current?.chart;
				const strategiesChart = strategiesChartRef.current?.chart;

				if (strategiesChart?.xAxis) {
					const currentExtremes = strategiesChart.xAxis[0]?.getExtremes();
					console.log('📊 [setExtremes] strategiesChart BEFORE:', {
						currentMin: currentExtremes?.min,
						currentMax: currentExtremes?.max,
						currentMinDate: currentExtremes?.min ? new Date(currentExtremes.min).toISOString() : null,
						currentMaxDate: currentExtremes?.max ? new Date(currentExtremes.max).toISOString() : null,
						dataMin: currentExtremes?.dataMin,
						dataMax: currentExtremes?.dataMax,
					});
					strategiesChart.xAxis.forEach((axis) => axis.setExtremes(newMin, newMax));
					const afterExtremes = strategiesChart.xAxis[0]?.getExtremes();
					console.log('✅ [setExtremes] strategiesChart AFTER:', {
						newMin: afterExtremes?.min,
						newMax: afterExtremes?.max,
						newMinDate: afterExtremes?.min ? new Date(afterExtremes.min).toISOString() : null,
						newMaxDate: afterExtremes?.max ? new Date(afterExtremes.max).toISOString() : null,
					});
				} else if (chart?.xAxis) {
					const currentExtremes = chart.xAxis[0]?.getExtremes();
					console.log('📊 [setExtremes] chart BEFORE:', {
						currentMin: currentExtremes?.min,
						currentMax: currentExtremes?.max,
						currentMinDate: currentExtremes?.min ? new Date(currentExtremes.min).toISOString() : null,
						currentMaxDate: currentExtremes?.max ? new Date(currentExtremes.max).toISOString() : null,
						dataMin: currentExtremes?.dataMin,
						dataMax: currentExtremes?.dataMax,
					});
					chart.xAxis.forEach((axis) => axis.setExtremes(newMin, newMax));
					const afterExtremes = chart.xAxis[0]?.getExtremes();
					console.log('✅ [setExtremes] chart AFTER:', {
						newMin: afterExtremes?.min,
						newMax: afterExtremes?.max,
						newMinDate: afterExtremes?.min ? new Date(afterExtremes.min).toISOString() : null,
						newMaxDate: afterExtremes?.max ? new Date(afterExtremes.max).toISOString() : null,
					});
				} else {
					console.warn('⚠️ [setExtremes] No chart available');
				}
			}, 300);

			return () => clearTimeout(timer);
		}
	}, [dashboardContext.selectedDate]);

	// ✅ Disable strategies switch when location changes
	useEffect(() => {
		if (dashboardContext?.selectedLocation && dashboardDataContext.showStrategies) {
			dashboardDataContext.setShowStrategies(false);
		}
	}, [dashboardContext.selectedLocation]);

	// ✅ Reflow chart when transitioning between expanded and compact views
	useEffect(() => {
		// Add a small delay to ensure the container has finished resizing
		const timer = setTimeout(() => {
			const chart = chartRef?.current?.chart;
			const strategiesChart = strategiesChartRef?.current?.chart;

			if (chart) {
				try {
					chart.reflow();
					chart.redraw();
				} catch (error) {
					console.warn('Error reflowing chart:', error);
				}
			}

			if (strategiesChart && strategiesChart !== chart) {
				try {
					strategiesChart.reflow();
					strategiesChart.redraw();
				} catch (error) {
					console.warn('Error reflowing strategies chart:', error);
				}
			}
		}, 200);

		return () => clearTimeout(timer);
	}, [dashboardContext.isExpandedParamsCard]);

	useEffect(() => {
		if (dashboardContext?.selectedDate && chartRef?.current) {
			// buildDataToGraph([]);
		}

		subscriptionPC$.subscribe((event) => {
			if (event.type == 'showReadingLoader') {
				expandedContext.setIsSpinnerReadingVisible(event.data);
			}
		});
		expandedContext.setPrimerRender(false);
	}, []);

	useEffect(() => {
		if (!expandedContext.primerRender) {
			dashboardDataContext.setSelectedParamsList([]);
			setDashboardContext({
				...dashboardContext,
				isExpandedParamsCard: false,
				selectedSensors: [],
				showParamsCard: false,
			});
			dashboardDataContext.setSourcesByParam([]);
			dashboardDataContext.setSelectedRowKeys([]);
			dashboardDataContext.setShowSources(false);
			dashboardDataContext.setSelectedSources([]);
		}
	}, [dashboardContext.selectedLocation]);

	useEffect(() => {
		const currentIds = new Set(
			resumeList
				.filter((item) => !!item)
				.map((item) => {
					return item.parameterId;
				}),
		);
		const newVisibilityState = new Map(dashboardDataContext.seriesVisibilityState);

		currentIds.forEach((id: any) => {
			if (!newVisibilityState.has(id)) {
				newVisibilityState.set(id, true);
			}
		});

		const keysToRemove: any = [];
		newVisibilityState.forEach((value, key) => {
			if (!currentIds.has(key)) {
				keysToRemove.push(key);
			}
		});
		keysToRemove.forEach((key) => newVisibilityState.delete(key));

		if (
			newVisibilityState.size !== dashboardDataContext.seriesVisibilityState.size ||
			keysToRemove.length > 0 ||
			[...currentIds].some((id: any) => !dashboardDataContext.seriesVisibilityState.has(id))
		) {
			dashboardDataContext.setSeriesVisibilityState(newVisibilityState);
		}

		const newDataMap = new Map();
		resumeList
			.filter((item) => !!item)
			.forEach((item) => {
				newDataMap.set(item.key || item.parameterId, item);
			});
		previousDataRef.current = newDataMap;
	}, [resumeList, dashboardDataContext.seriesVisibilityState]);

	const handleCurrentQuality = useMemo(() => {
		if (!dates || !sourceToAnnotation) return undefined;
		return checkQualityForNewAnnotation(dashboardDataContext.selectedQualityAnnotationRange);
	}, [dashboardDataContext.selectedQualityAnnotationRange]);

	const memoizedSourceList = useMemo(
		() => (
			<SourceList
				sourcesByParam={dashboardDataContext.sourcesByParam}
				isCardColumnView={true}
				disabledList={!dashboardDataContext.showSources}
				key={`sourcelist-${dashboardDataContext.sourcesByParam.length}-${dashboardDataContext.sourcesByParam.map((s) => s.id).join('-')}`}
			/>
		),
		[dashboardDataContext.sourcesByParam, dashboardDataContext.showSources],
	);

	const memoizedSetIsModalOpen = useCallback((value: boolean) => setIsReportModalOpen(value), []);
	const memoizedToggleSeriesVisibility = useCallback(
		dashboardDataContext.toggleSeriesVisibility,
		[],
	);
	const memoizedSetSourcesByParam = useCallback(dashboardDataContext.setSourcesByParam, []);
	const memoizedSetSelectedParamsList = useCallback(dashboardDataContext.setSelectedParamsList, []);
	const memoizedSetContext = useCallback(setDashboardContext, []);

	const resumeTableComponent = useMemo(() => {
		if (!dashboardContext.showParamsCard || dashboardContext.isExpandedParamsCard) {
			return null;
		}

		return (
			<ResumeTable
				resumeList={dataTable}
				isSpinnerResumeVisible={expandedContext.isSpinnerReadingVisible}
				setIsReportModalOpen={memoizedSetIsModalOpen}
				sourceList={memoizedSourceList}
				toggleSeriesVisibility={memoizedToggleSeriesVisibility}
				setSourcesByParam={memoizedSetSourcesByParam}
				setSelectedParamsList={memoizedSetSelectedParamsList}
				setDashboardContext={memoizedSetContext}
			/>
		);
	}, [
		dashboardContext.showParamsCard,
		dashboardContext.isExpandedParamsCard,
		dataTable,
		expandedContext.isSpinnerReadingVisible,
		setIsReportModalOpen,
		dashboardDataContext.showSources,
		dashboardDataContext.sourcesByParam,
		annotationSaved,
		handleAnnotationToggle,
		activeAnnotationIcon,
		dashboardDataContext.toggleSeriesVisibility,
		dashboardDataContext.setSourcesByParam,
		dashboardDataContext.setSelectedParamsList,
		setDashboardContext,
	]);

	const memoizedReadingsGraph = useMemo(() => {
		const chartOptions = dashboardContext.isExpandedParamsCard
			? graphOptions
			: {
					...graphOptions,
					chart: {
						...graphOptions?.chart,
						cursor: annotationMode ? 'crosshair' : 'default',
					},
					xAxis: {
						...graphOptions?.xAxis,
						plotBands:
							graphOptions?.xAxis?.plotBands?.filter(
								(band) =>
									// Only keep the forecast band (color #EFFFFF), remove annotations
									band.color === '#EFFFFF',
							) || [],
					},
				};

		if (dashboardContext.isExpandedParamsCard) {
			return (
				<SynchronizedCharts
					readingsGraphOptions={chartOptions}
					strategiesChartRef={strategiesChartRef}
					isSpinnerReadingVisible={expandedContext.isSpinnerReadingVisible}
					strategiesData={strategiesResponse}
					dateFrom={dashboardContext.selectedDate[0]}
					dateTo={dashboardContext.selectedDate[1]}
					showStrategies={dashboardDataContext.showStrategies}
					annotationMode={annotationMode}
				/>
			);
		}

		return (
			<ReadingsGraph
				chartRef={chartRef}
				graphOptions={chartOptions}
				isSpinnerReadingVisible={expandedContext.isSpinnerReadingVisible}
			/>
		);
	}, [
		chartRef,
		strategiesChartRef,
		graphOptions,
		expandedContext.isSpinnerReadingVisible,
		dashboardContext.isExpandedParamsCard,
		strategiesResponse,
		dashboardDataContext.showStrategies,
		dashboardContext.selectedDate,
	]);

	return (
		<>
			{/* ✅ CSS para deshabilitar pointer-events en plotbands de anotaciones, pero permitir en notas */}
			{/* <style>{`
				.plotband-no-pointer-events {
					pointer-events: none !important;
				}
				.highcharts-plot-band-note {
					pointer-events: auto !important;
					cursor: pointer !important;
				}
				.highcharts-series,
				.highcharts-markers,
				.highcharts-point {
					pointer-events: auto !important;
				}
			`}</style> */}
			<ExpandedParamsCardView
				topRef={topRef}
				onCardHeightChange={(height) => {
					setDashboardContext((prev) => ({ ...prev, compactCardHeight: height }));
				}}
				context={{
					isExpandedParamsCard: dashboardContext.isExpandedParamsCard,
					showParamsCard: dashboardContext.showParamsCard,
					selectedSensors: dashboardContext.selectedSensors,
					selectedDate: dashboardContext.selectedDate,

					selectedSources: dashboardDataContext.selectedSources,
					selectedParamsList: dashboardDataContext.selectedParamsList,
					sourcesByParam: dashboardDataContext.sourcesByParam,
					isLoading: expandedContext.isLoading,
					isSpinnerReadingVisible: expandedContext.isSpinnerReadingVisible,
					primerRender: expandedContext.primerRender,
				}}
				setContext={{
					setIsExpandedParamsCard: (value) => {
						setDashboardContext({
							...dashboardContext,
							isExpandedParamsCard: value,
						});
					},
					setShowParamsCard: (value) => {
						setDashboardContext({
							...dashboardContext,
							showParamsCard: value,
						});
					},
				}}
				chartComponent={
					<div
						className="self-center flex flex-col"
						style={{
							width: '-webkit-fill-available',
							height: dashboardContext.isExpandedParamsCard ? 'calc(100vh - 320px)' : '100%',
							minHeight: dashboardContext.isExpandedParamsCard ? '250px' : '0',
							maxHeight: dashboardContext.isExpandedParamsCard ? 'none' : '100%',
							marginTop: dashboardContext.isExpandedParamsCard ? '10px' : '0px',
							padding: dashboardContext.isExpandedParamsCard ? '10px' : '0px',
							overflow: 'visible',
						}}
					>
						{memoizedReadingsGraph}
					</div>
				}
				resumeTableComponent={resumeTableComponent}
				sourcesListComponent={
					dashboardDataContext.showSources && dashboardContext.isExpandedParamsCard ? (
						<SourceList
							key={`expanded-sourcelist-${dashboardDataContext.sourcesByParam.length}-${dashboardDataContext.sourcesByParam.map((s) => s.id).join('-')}`}
							sourcesByParam={dashboardDataContext.sourcesByParam}
							disabledList={!dashboardDataContext.showSources}
							enabledAnnotation={true}
							resetAnnotationIcon={annotationSaved}
							onAnnotationToggle={handleAnnotationToggle}
							activeAnnotationIcon={activeAnnotationIcon}
						/>
					) : null
				}
				tableComponent={
					<ParameterTable
						dataTable={paramsToShowInTable()}
						rowSelectionState={rowSelectionState}
						onRowSelectionChange={handleRowSelectionChange}
						expandedRowKeys={expandedRowKeys}
						columnsExpanded={columnsExpanded}
						onSearch={setQueryParams}
						setIsReportModalOpen={setIsReportModalOpen}
						disableDownloadReport={disableDownloadReport}
						notesForParams={
							<div
								className={'flex cursor-pointer justify-center items-center ml-2'}
								style={{ color: noteClicked ? '#078cb3' : '#374151' }}
								onClick={() => {
									if (noteClicked) {
										setNoteClicked(false);
										setAnnotationMode(false);
										setIsParamAnnotation(false);
									} else {
										setNoteClicked(true);
										setAnnotationMode(true);
										scrollToTop(topRef);
										setIsParamAnnotation(true);
									}
								}}
							>
								<Tooltip position='up' trigger="hover" text={'Add an Operational Note to add comments across one or more parameters. Drag the mouse over the chart to create one.'}>
									<span className={'mr-1'}>Create an Operational Note</span>
									<FormOutlined style={{ color: noteClicked ? '#078cb3' : '#374151' }} />
								</Tooltip>
							</div>
						}
					/>
				}
				forecastSelectorComponent={
					<ForecastSelector
						showButton={showButton}
						isDateRangeMoreThan24Hours={isDateRangeMoreThan24Hours}
						showPastForecast={showPastForecast}
						setShowPastForecast={setShowPastForecast}
						selectedSimulation={selectedSimulation}
						setSelectedSimulation={setSelectedSimulation}
						onFetchSimulations={fetchSimulations}
						cleanAreaSeries={cleanAreaSeries}
						simulationList={simulationsList}
						isLoadingSimulations={isLoadingSimulations}
						hasSimulationError={hasSimulationError}
					/>
				}
				switchShowSources={
					<>
						<Toggle
							checked={dashboardDataContext.showSources}
							checkedText="Show Sources"
							uncheckedText="Show Sources"
							size="small"
							onChange={({ target: { checked } }) => {
								if (!checked) {
									dashboardDataContext.setSourcesByParam((currentSources: any[]) => {
										const updatedSources = currentSources.map((param) => {
											return {
												...param,
												sources: param.sources.map((source: any) => {
													if (source.name === 'Recommended' && source.sourceId === null) {
														if (!source.checked && source.onCheck) {
															source.onCheck(true, source.sourceId, source.serialNumber);
														}
														return {
															...source,
															checked: true,
															_forceUpdate: Date.now(),
														};
													} else {
														if (source.onCheck) {
															source.onCheck(false, source.sourceId, source.serialNumber);
														}

														return {
															...source,
															checked: false,

															_forceUpdate: Date.now(),
														};
													}
												}),
											};
										});
										return updatedSources;
									});

									// ✅ Also activate parameters in the table
									dashboardDataContext.setSelectedRowKeys((currentKeys: any[]) => {
										const parameterIds = dashboardDataContext.sourcesByParam.map(
											(param) => param.id,
										);
										// Add all parameters that have recommended sources
										const newKeys = [...new Set([...currentKeys, ...parameterIds])];

										return newKeys;
									});
								}
								dashboardDataContext.setShowSources(checked);
							}}
							style={{ width: '110px' }}
						/>
					</>
				}
				strategiesSwitchComponent={
					<Tooltip
						title={
							!hasValidStrategies
								? 'There are no inp models with smart controls enabled and at least one non-default strategy'
								: 'Recommended strategies'
						}
						position="down-right"
						trigger="hover"
					>
						<span>
							<Toggle
								checked={dashboardDataContext.showStrategies}
								checkedText="Recommended Strategies"
								uncheckedText="Recommended Strategies"
								size="small"
								disabled={!hasValidStrategies}
								onChange={({ target: { checked } }) => {
									// ✅ Solo cambiar el estado del toggle, las estrategias ya están cargadas
									dashboardDataContext.setShowStrategies(checked);
								}}
							/>
						</span>
					</Tooltip>
				}
				strategiesChartComponent={null}
				isLoading={isLoading}
				resetZoomComponent={
					<Button
						showIcon={false}
						className="mr-2"
						variant="primary"
						size="small"
						onClick={resetZoom}
						label="Reset zoom"
					/>
				}
			/>
			<NotesForParamsModal
				startDate={dates?.[0]}
				endDate={dates?.[1]}
				openModal={showParamAnnotationModal}
				setOpenModal={setShowParamAnnotationModal}
				onSuccess={() => {
					setShowParamAnnotationModal(false);
					setIsParamAnnotation(false);
					handleGetAllReadings();

					// ✅ Recargar notes para TODOS los parámetros con algún source activo
					dashboardDataContext.sourcesByParam.forEach((param: any) => {
						// Verificar si hay algún source activo (checked=true)
						const hasActiveSource = param.sources?.some((source: any) => source.checked === true);

						if (hasActiveSource) {
							// Buscar primer source real para obtener las notes
							const firstRealSource = param.sources?.find(
								(source: any) => source.name !== 'Recommended' && source.sourceId !== null,
							);

							if (firstRealSource) {
								handleGetAnnotations(
									firstRealSource.sourceId?.toString() || '',
									param.id,
									firstRealSource.serialNumber,
									true, // isRecommended = true para cargar notes
								);
							}
						}
					});

					setNoteClicked(false);
				}}
				onCancel={() => {
					setAnnotationMode(false);
					setActiveAnnotationIcon(null);
					setNoteClicked(false);
					setIsParamAnnotation(false);

					setAnnotationSaved((prev) => !prev);
				}}
				paramList={paramsToShowInTable().map((param) => {
					return `${param.parameterId}`;
				})}
			/>
			<Modal
				isOpen={showAnnotationModal}
				onClose={() => setShowAnnotationModal(false)}
				draggable={false}
				size="m"
			>
				<Modal.Header>
					<Modal.Title text="ADD DATA ANNOTATIONS" />
				</Modal.Header>

				<Modal.Content>
					<AnnotationForm
						title={title}
						quality={quality}
						comment={comment}
						onTitleChange={setTitle}
						onQualityChange={setQuality}
						onCommentChange={setComment}
						onSave={handleSaveSourcesAnnotation}
						onCancel={() => {
							setShowAnnotationModal(false);

							setAnnotationMode(false);
							dashboardDataContext.setSelectedSourceToAnnotation('');
							setActiveAnnotationIcon(null);

							setAnnotationSaved((prev) => !prev);
							setTitle('');
							setComment('');
							setQuality(0);
						}}
						currentQuality={handleCurrentQuality}
						loading={isSavingAnnotation}
					/>
				</Modal.Content>
			</Modal>
			<Modal
				isOpen={showAnnotationListModal}
				onClose={() => setShowAnnotationListModal(false)}
				draggable={false}
				size="xl"
			>
				<Modal.Header>
					<Modal.Title text="Notes and Annotations" />
				</Modal.Header>
				<Modal.Content>
					<AnnotationsList columns={columnsAnnotationsModal} annotations={annotationListToShow} />
				</Modal.Content>
			</Modal>
			<DownloadReportModal
				isOpen={isReportModalOpen}
				onClose={() => setIsReportModalOpen(false)}
				onDownload={() => {
					generateReport(sensorsForReport);
					setIsReportModalOpen(false);
				}}
			/>
		</>
	);
};
